/* Aus gelesenen FGD-Klassen die Strukturen des Editors bauen: Klassenliste (EntDef) und Eigenschaften-Beschreibung (Spec). Ohne DOM. */
import type { EntDef } from './entdefs.ts';
import type { Field, Flag, Opt, Spec } from './entspec.ts';
import type { FgdField, FgdResolved, FgdSet } from './fgd.ts';
import { loadFgds } from './fgd.ts';

/** FGD-Typ → Feldtyp des Editors (siehe entspec.ts). Unbekannte und noch nicht abbildbare Typen werden Textfelder. */
const TYPE: Record<string, string> = {
  string: 's', integer: 'i', float: 'f', choices: 'c', color255: 'col',
  target_source: 't', target_destination: 'T', sound: 'snd', sprite: 'spr', sky: 'sky'
};

/** Typen, die als Textfeld erscheinen, obwohl Hammer mehr kann (für docs/fgd-gaps.md und Tests) */
export const PLAIN_TEXT_TYPES = ['studio', 'color1', 'decal', 'angle', 'sidelist', 'axis', 'origin', 'vecline', 'material', 'filterclass', 'flags'];

export const hexOfRgb = (c: number[]): string => '#' + c.map((x) => Math.max(0, Math.min(255, x)).toString(16).padStart(2, '0')).join('');

/** Feldtyp des Editors zu einem FGD-Feld */
export function fieldType(f: FgdField): string {
  if (f.type === 'choices' && !(f.choices && f.choices.length)) return 's';
  return TYPE[f.type] || 's';
}

function toField(f: FgdField): Field {
  const type = fieldType(f);
  const label = f.label || f.key;
  const opts: Opt[] | null = type === 'c' ? (f.choices || []).map((c): Opt => [c.value, c.label || c.value]) : null;
  return [f.key, label, type, opts, f.dflt, true];
}

const HIDDEN = new Set(['origin', 'spawnflags']);

/** Beschreibung zu einer Klasse: Felder, Spawnflags, Text */
export function toSpec(c: FgdResolved): Spec {
  const k = c.fields.filter((f) => !HIDDEN.has(f.key.toLowerCase())).map(toField);
  const f: Flag[] = c.flags.map((x): Flag => [x.bit, x.label || String(x.bit), true]);
  return { d: c.desc, f, k, raw: true };
}

/** Eintrag der Klassenliste */
export function toEntDef(c: FgdResolved, groupOf: (name: string) => string, colors: Record<string, string>): EntDef {
  const g = groupOf(c.name);
  const initial = c.flags.reduce((n, x) => (x.on ? n | x.bit : n), 0);
  const d: Record<string, string> = {};
  if (initial) d.spawnflags = String(initial);
  const def: EntDef = { c: c.name, k: c.kind === 'solid' ? 'brush' : 'point', g, color: c.color ? hexOfRgb(c.color) : (colors[g] || colors.other), d, fgd: true };
  if (c.size) def.size = c.size;
  if (c.studio !== undefined) def.studio = c.studio;
  if (c.iconsprite) def.iconsprite = c.iconsprite;
  if (c.sprite !== undefined) def.sprite = c.sprite;
  if (c.desc) def.desc = c.desc;
  return def;
}

export interface FgdLibrary { defs: EntDef[]; specs: Record<string, Spec>; warnings: string[]; classes: number }

/** Klassen und Beschreibungen aus aufgelösten FGDs */
export function buildLibrary(set: FgdSet, groupOf: (name: string) => string, colors: Record<string, string>): FgdLibrary {
  const defs: EntDef[] = [], specs: Record<string, Spec> = {};
  for (const c of set.classes.values()) { defs.push(toEntDef(c, groupOf, colors)); specs[c.name] = toSpec(c); }
  return { defs, specs, warnings: set.warnings, classes: defs.length };
}

/** Aus den Texten mehrerer FGD-Dateien (in Ladereihenfolge) */
export function libraryFromTexts(texts: string[], groupOf: (name: string) => string, colors: Record<string, string>, names?: string[]): FgdLibrary {
  return buildLibrary(loadFgds(texts, names), groupOf, colors);
}
